import { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../lib/firebase';
import { Brain, Package, Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon, Zap, Settings } from 'lucide-react';
import { uploadToR2 } from '../../services/r2Service';
import AdminSettings from '../../components/AdminSettings';

interface AIPackage {
  id: string;
  name: string;
  version: string;
  price: number;
  description: string;
  features: string[];
  coverImage: string;
  active: boolean;
  order: number;
  createdAt: Date;
  updatedAt: Date;
}

export default function AISystemAdmin() {
  const [activeTab, setActiveTab] = useState<'packages' | 'settings'>('packages');
  const [packages, setPackages] = useState<AIPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<AIPackage | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    price: 0,
    description: '',
    features: [''],
    coverImage: '',
    active: true,
    order: 0
  });

  useEffect(() => {
    // Wait for auth state to be ready
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('🔐 Auth State Changed:', user);
      console.log('📧 Email:', user?.email);
      console.log('🆔 UID:', user?.uid);
      setCurrentUser(user);
      
      // Always load packages (even if not logged in, to show the UI)
      loadPackages();
      
      if (!user) {
        console.warn('⚠️ No user logged in - write operations will fail');
      }
    });

    return () => unsubscribe();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      console.log('🔍 Loading AI packages from Firestore...');
      const snapshot = await getDocs(collection(db, 'aiPackages'));
      console.log('📦 Snapshot size:', snapshot.size);
      console.log('📦 Docs:', snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      
      const packagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as AIPackage[];
      
      packagesData.sort((a, b) => a.order - b.order);
      console.log('✅ Loaded packages:', packagesData);
      setPackages(packagesData);
    } catch (error) {
      console.error('❌ Error loading packages:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultPackages = async () => {
    if (!confirm('สร้างแพ็คเก็จเริ่มต้น V1, V2, V3, V4 ใช่หรือไม่?')) {
      return;
    }

    setLoading(true);
    try {
      const defaultPackages = [
        {
          name: 'AIForYou V1',
          version: 'V1',
          price: 890,
          description: 'สร้าง Prompt คอนเทนต์',
          features: [
            'สร้าง Prompt สำหรับคอนเทนต์คุณภาพสูง',
            'เหมาะสำหรับ บทความ โพสต์ แคปชั่น',
            'ใช้งานง่าย เข้าใจง่าย',
            'รองรับภาษาไทยและภาษาอังกฤษ'
          ],
          coverImage: '',
          active: true,
          order: 1
        },
        {
          name: 'AIForYou V2',
          version: 'V2',
          price: 990,
          description: 'สร้าง Prompt รูปภาพ',
          features: [
            'สร้าง Prompt สำหรับ AI Image Generator',
            'รองรับหลากหลายสไตล์',
            'ได้ผลลัพธ์ที่สวยงามและสมจริง',
            'เหมาะสำหรับงานกราฟิกและดีไซน์'
          ],
          coverImage: '',
          active: true,
          order: 2
        },
        {
          name: 'AIForYou V3',
          version: 'V3',
          price: 0,
          description: 'สร้าง Prompt ขาย Ebook',
          features: [
            'วางโครงสร้าง Ebook อย่างมืออาชีพ',
            'สร้างเนื้อหาที่น่าสนใจและมีคุณค่า',
            'เหมาะสำหรับการขาย Ebook',
            'Coming Soon - เร็วๆ นี้'
          ],
          coverImage: '',
          active: false,
          order: 3
        },
        {
          name: 'AIForYou V4',
          version: 'V4',
          price: 0,
          description: 'สร้าง Prompt + แนวคิดสร้างแอป',
          features: [
            'วางแผนและออกแบบแอปพลิเคชัน',
            'สร้าง Prompt สำหรับ AI ช่วยพัฒนา',
            'แนวคิดและกลยุทธ์การสร้างแอป',
            'Coming Soon - เร็วๆ นี้'
          ],
          coverImage: '',
          active: false,
          order: 4
        }
      ];

      for (const pkg of defaultPackages) {
        const docRef = doc(db, 'aiPackages', pkg.version.toLowerCase());
        await setDoc(docRef, {
          ...pkg,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      alert('✅ สร้างแพ็คเก็จเริ่มต้นสำเร็จ!');
      loadPackages();
    } catch (error) {
      console.error('Error initializing packages:', error);
      alert('เกิดข้อผิดพลาด: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('ไฟล์รูปภาพต้องมีขนาดไม่เกิน 5MB');
        return;
      }
      
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.version || formData.price < 0) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    setUploading(true);
    try {
      let coverImageUrl = formData.coverImage;

      if (imageFile) {
        console.log('🔄 Uploading image to R2...');
        const uploadResult = await uploadToR2(imageFile, 'admin', 'ai-packages');
        if (uploadResult.success && uploadResult.url) {
          coverImageUrl = uploadResult.url;
          console.log('✅ Image uploaded:', coverImageUrl);
        } else {
          console.warn('⚠️ Image upload failed:', uploadResult.error);
          // ไม่ throw error แต่แจ้งเตือนผู้ใช้
          if (!confirm(`ไม่สามารถอัพโหลดรูปภาพได้: ${uploadResult.error}\n\nต้องการบันทึกแพ็คเก็จโดยไม่มีรูปภาพหรือไม่?`)) {
            setUploading(false);
            return;
          }
        }
      }

      const packageData = {
        name: formData.name,
        version: formData.version,
        price: Number(formData.price),
        description: formData.description,
        features: formData.features.filter(f => f.trim() !== ''),
        coverImage: coverImageUrl,
        active: formData.active,
        order: Number(formData.order),
        updatedAt: new Date()
      };

      if (!currentUser) {
        alert('กรุณา login ก่อนทำรายการ');
        setUploading(false);
        return;
      }

      if (editingPackage) {
        console.log('✏️ Updating package:', editingPackage.id);
        console.log('👤 Current user:', currentUser?.email, currentUser?.uid);
        await updateDoc(doc(db, 'aiPackages', editingPackage.id), packageData);
        alert('อัพเดตแพ็คเก็จสำเร็จ');
      } else {
        console.log('➕ Creating new package');
        console.log('👤 Current user:', currentUser?.email, currentUser?.uid);
        const newDocRef = doc(collection(db, 'aiPackages'));
        await setDoc(newDocRef, {
          ...packageData,
          createdAt: new Date()
        });
        alert('สร้างแพ็คเก็จสำเร็จ');
      }

      setShowModal(false);
      resetForm();
      loadPackages();
    } catch (error) {
      console.error('❌ Error saving package:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`เกิดข้อผิดพลาดในการบันทึก: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (pkg: AIPackage) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      version: pkg.version,
      price: pkg.price,
      description: pkg.description,
      features: pkg.features.length > 0 ? pkg.features : [''],
      coverImage: pkg.coverImage,
      active: pkg.active,
      order: pkg.order
    });
    setImagePreview(pkg.coverImage);
    setShowModal(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`ต้องการลบแพ็คเก็จ "${name}" หรือไม่?`)) {
      return;
    }

    if (!currentUser) {
      alert('กรุณา login ก่อนทำรายการ');
      return;
    }

    try {
      console.log('🗑️ Deleting package:', id);
      console.log('👤 Current user:', currentUser?.email, currentUser?.uid);
      await deleteDoc(doc(db, 'aiPackages', id));
      alert('ลบแพ็คเก็จสำเร็จ');
      loadPackages();
    } catch (error) {
      console.error('❌ Error deleting package:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`เกิดข้อผิดพลาดในการลบ: ${errorMessage}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      version: '',
      price: 0,
      description: '',
      features: [''],
      coverImage: '',
      active: true,
      order: 0
    });
    setEditingPackage(null);
    setImageFile(null);
    setImagePreview('');
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin AIForYou</h2>
            <p className="text-sm text-gray-600">ระบบ AIForYou</p>
          </div>
        </div>
        {activeTab === 'packages' && (
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>เพิ่มแพ็คเก็จ</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('packages')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'packages'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Package className="w-5 h-5" />
            <span>จัดการแพ็คเก็จ AI</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'settings'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>ตั้งค่า</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'settings' ? (
        <AdminSettings />
      ) : (
        <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">แพ็คเก็จทั้งหมด</p>
              <p className="text-2xl font-bold text-gray-900">{packages.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">แพ็คเก็จที่เปิดใช้งาน</p>
              <p className="text-2xl font-bold text-gray-900">{packages.filter(p => p.active).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">ราคาเฉลี่ย</p>
              <p className="text-2xl font-bold text-gray-900">
                ฿{packages.length > 0 ? Math.round(packages.reduce((sum, p) => sum + p.price, 0) / packages.length) : 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Packages List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all"
          >
            {/* Cover Image */}
            <div className="relative h-48 bg-gradient-to-br from-purple-500 to-indigo-600">
              {pkg.coverImage ? (
                <img
                  src={pkg.coverImage}
                  alt={pkg.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-16 h-16 text-white/50" />
                </div>
              )}
              {!pkg.active && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="px-3 py-1 bg-red-500 text-white text-sm font-semibold rounded-full">
                    ปิดใช้งาน
                  </span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                  <p className="text-sm text-purple-600 font-semibold">{pkg.version}</p>
                </div>
                <span className="text-sm text-gray-500">#{pkg.order}</span>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{pkg.description}</p>

              <div className="mb-4">
                <p className="text-2xl font-bold text-gray-900">฿{pkg.price.toLocaleString()}</p>
              </div>

              {pkg.features.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">ฟีเจอร์:</p>
                  <ul className="space-y-1">
                    {pkg.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                        <span className="text-purple-600 mt-0.5">•</span>
                        <span className="line-clamp-1">{feature}</span>
                      </li>
                    ))}
                    {pkg.features.length > 3 && (
                      <li className="text-xs text-gray-400">+{pkg.features.length - 3} อื่นๆ</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg font-medium transition-all"
                >
                  <Edit className="w-4 h-4" />
                  <span>แก้ไข</span>
                </button>
                <button
                  onClick={() => handleDelete(pkg.id, pkg.name)}
                  className="flex items-center justify-center px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {packages.length === 0 && !loading && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Brain className="w-10 h-10 text-purple-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">ยังไม่มีแพ็คเก็จ AI</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            เริ่มต้นสร้างแพ็คเก็จ AIForYou แรกของคุณ เช่น V1, V2, V3, V4
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={initializeDefaultPackages}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <Zap className="w-5 h-5" />
              <span>สร้างแพ็คเก็จเริ่มต้น (V1-V4)</span>
            </button>
            
            <span className="text-gray-400">หรือ</span>
            
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>สร้างแพ็คเก็จเอง</span>
            </button>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-4">แพ็คเก็จเริ่มต้นที่จะถูกสร้าง:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {['V1 - Prompt คอนเทนต์ (฿890)', 'V2 - Prompt รูปภาพ (฿990)', 'V3 - Ebook (Coming Soon)', 'V4 - สร้างแอป (Coming Soon)'].map((example, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                  {example}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editingPackage ? 'แก้ไขแพ็คเก็จ' : 'เพิ่มแพ็คเก็จใหม่'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Cover Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  รูปภาพหน้าปก
                </label>
                <div className="space-y-3">
                  {imagePreview && (
                    <div className="relative w-full h-48 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer transition-all">
                    <Upload className="w-5 h-5 text-gray-600" />
                    <span className="text-sm text-gray-600 font-medium">
                      {imagePreview ? 'เปลี่ยนรูปภาพ' : 'อัพโหลดรูปภาพ'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-gray-500">รองรับ JPG, PNG (ไม่เกิน 5MB)</p>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ชื่อแพ็คเก็จ *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="เช่น AIForYou V1"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Version */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  เวอร์ชัน *
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="เช่น V1"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ราคา (บาท) *
                </label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow only numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      setFormData({ ...formData, price: value === '' ? 0 : Number(value) });
                    }
                  }}
                  placeholder="890"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  คำอธิบาย
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="อธิบายแพ็คเก็จ..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    ฟีเจอร์
                  </label>
                  <button
                    onClick={addFeature}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    + เพิ่มฟีเจอร์
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        placeholder={`ฟีเจอร์ ${index + 1}`}
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      {formData.features.length > 1 && (
                        <button
                          onClick={() => removeFeature(index)}
                          className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ลำดับการแสดงผล
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                  placeholder="0"
                  min="0"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Active */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                />
                <label htmlFor="active" className="text-sm font-medium text-gray-700">
                  เปิดใช้งานแพ็คเก็จนี้
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSave}
                disabled={uploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>กำลังบันทึก...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>บันทึก</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
